import { FC, ReactElement, useEffect, useState, memo } from 'react'
import { useSwipeable } from 'react-swipeable'

import { testimonialsDataMock } from '../../../__mock__/testimonialsDataMock'
import { useI18n } from '../../../hooks'
import './index.css'

export interface Testimonial {
  id: number
  name: string
  content: string
}

const TestimonialsCard: FC<{ testimonial: Testimonial }> = ({ testimonial }): ReactElement => {
  return (
    <div className="testimonials-card">
      <p className="testimonials-card-content">"{testimonial.content}"</p>
      <p className="testimonials-card-name">{testimonial.name}</p>
    </div>
  )
}

interface TestimonialsCarouselSlide {
  element: ReactElement
  class: string
}

const TestimonialsCarousel: FC = (): ReactElement => {
  const [slideCurrent, setSlideCurrent] = useState(0)
  const [slides, setSlides] = useState<TestimonialsCarouselSlide[]>([])
  const { t } = useI18n()

  useEffect(() => {
    const newSlides: TestimonialsCarouselSlide[] = testimonialsDataMock.slice(0, 5).map((testimonial, index) => {
      return {
        element: <TestimonialsCard testimonial={testimonial} />,
        class:
          index === 0
            ? 'testimonials-carousel-item-active'
            : index > 0
            ? 'testimonials-carousel-item-next'
            : 'testimonials-carousel-item-pre',
      }
    })
    if (newSlides.length > 0) {
      const { nextSlideIndex, preSlideIndex } = getSlideIndex(slideCurrent)
      newSlides[slideCurrent].class = 'testimonials-carousel-item-active'
      newSlides[preSlideIndex].class = 'testimonials-carousel-item-pre'
      newSlides[nextSlideIndex].class = 'testimonials-carousel-item-next'
      setSlides(newSlides)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getSlideIndex = (currentIndex: number) => {
    const nextSlideIndex = currentIndex === testimonialsDataMock.length - 1 ? 0 : currentIndex + 1
    const preSlideIndex = currentIndex === 0 ? testimonialsDataMock.length - 1 : currentIndex - 1
    return { nextSlideIndex, preSlideIndex }
  }

  const setNewSlide = (activeIndex: number) => {
    const newSlides = [...slides]
    newSlides.forEach((slid, index) => {
      if (index === activeIndex) {
        slid.class = 'testimonials-carousel-item-active'
      } else if (index < activeIndex) {
        slid.class = 'testimonials-carousel-item-pre'
      } else {
        slid.class = 'testimonials-carousel-item-next'
      }
    })
    const { nextSlideIndex, preSlideIndex } = getSlideIndex(activeIndex)
    newSlides[activeIndex].class = 'testimonials-carousel-item-active'
    newSlides[preSlideIndex].class = 'testimonials-carousel-item-pre'
    newSlides[nextSlideIndex].class = 'testimonials-carousel-item-next'
    setSlides(newSlides)
    setSlideCurrent(activeIndex)
  }

  const slideLeft = () => {
    const newIndext = getSlideIndex(slideCurrent).preSlideIndex
    setNewSlide(newIndext)
  }

  const slideRight = () => {
    const newIndext = getSlideIndex(slideCurrent).nextSlideIndex
    setNewSlide(newIndext)
  }
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => slideRight(),
    onSwipedRight: () => slideLeft(),
  })
  return (
    <div className="testimonials-carousel" {...swipeHandlers} style={{ touchAction: 'pan-y' }}>
      <h1 className="testimonials-carousel-title">{t('Common_testimonials')}</h1>
      <div className="testimonials-carousel-title-line"></div>
      <div className="testimonials-carousel-arrows">
        <button className="testimonials-carousel-arrow-left" onClick={slideLeft} aria-label="testimonial arrow left">
          <i className="feather-chevron-left"></i>
        </button>
        <button className="testimonials-carousel-arrow-right" onClick={slideRight} aria-label="testimonial arrow right">
          <i className="feather-chevron-right"></i>
        </button>
      </div>
      <div className="testimonials-carousel-contener">
        {slides.map((slide, i) => (
          <div key={`testimonial-slid-${i}`} className={`testimonials-carousel-item ${slide.class}`}>
            {slide.element}
          </div>
        ))}
      </div>
      <div className="testimonials-carousel-dots">
        {slides.map((slide, index) => {
          const className = slide.class.includes('active') ? 'testimonials-carousel-dot-active' : ''
          return (
            <button
              key={`testimonial-dot-${index}`}
              onClick={() => setNewSlide(index)}
              className={`testimonials-carousel-dot ${className}`}
              title={`card-${index}-selector`}
            ></button>
          )
        })}
      </div>
    </div>
  )
}

export default memo(TestimonialsCarousel)
